import { useEffect, useMemo, useState } from 'react';
import { api } from '../../api';
import type { ConversationListEntry, StarredMessage } from '../../types';
import { useChat } from '../../store';
import { Avatar } from '../Avatar';
import { IconSearch, IconStar } from '../Icon';

interface InfoStarredProps {
  conversation: ConversationListEntry;
  onJumped?: () => void;
}

export function InfoStarred({ conversation, onJumped }: InfoStarredProps) {
  const users = useChat((s) => s.users);
  const me = useChat((s) => s.me);
  const setJumpToMessage = useChat((s) => s.setJumpToMessage);
  const [items, setItems] = useState<StarredMessage[] | null>(null);
  const [filter, setFilter] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .conversationStarred(conversation.id)
      .then((r) => {
        if (!cancelled) setItems(r.messages);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [conversation.id]);

  const visible = useMemo(() => {
    if (!items) return null;
    const q = filter.trim().toLowerCase();
    if (!q) return items;
    return items.filter((m) => (m.body ?? '').toLowerCase().includes(q));
  }, [items, filter]);

  function senderName(id: string): string {
    if (id === me?.id) return 'You';
    const u = users.find((x) => x.id === id);
    return u?.displayName ?? u?.email ?? 'Unknown';
  }

  function jumpTo(id: string) {
    setJumpToMessage(id);
    onJumped?.();
  }

  async function unstar(id: string) {
    setBusyId(id);
    try {
      await api.unstarMessage(id);
      setItems((prev) => prev?.filter((m) => m.id !== id) ?? null);
    } catch {
      /* ignore */
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="px-4 py-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-wide text-zinc-500">
          {items ? `${items.length} starred` : 'Loading…'}
        </p>
        <button
          onClick={() => setShowSearch((v) => !v)}
          className="p-1.5 rounded-md text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 transition"
          aria-label="Search starred messages"
        >
          <IconSearch size={14} />
        </button>
      </div>

      {showSearch && (
        <input
          autoFocus
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search starred…"
          className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      )}

      {visible && visible.length === 0 && (
        <div className="py-10 text-center">
          <IconStar size={28} className="mx-auto text-zinc-600 mb-2" />
          <p className="text-sm text-zinc-400">No starred messages</p>
          <p className="text-[11px] text-zinc-500 mt-1 max-w-[16rem] mx-auto">
            Star important messages so you can quickly find them later.
          </p>
        </div>
      )}

      <ul className="space-y-2">
        {visible?.map((m) => (
          <li
            key={m.id}
            className="rounded-md border border-zinc-800 bg-zinc-900/50 hover:bg-zinc-900 transition flex gap-3"
          >
            <button
              onClick={() => jumpTo(m.id)}
              className="flex-1 min-w-0 flex gap-3 px-3 py-2.5 text-left"
              title="Jump to message"
            >
              <Avatar
                id={m.senderId}
                displayName={senderName(m.senderId)}
                email={senderName(m.senderId)}
                size={28}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-0.5">
                  <p className="text-xs font-medium truncate">{senderName(m.senderId)}</p>
                  <span className="text-[10px] text-zinc-500 shrink-0">
                    {new Date(m.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-sm text-zinc-200 whitespace-pre-wrap break-words line-clamp-3">
                  {m.body ?? <span className="italic text-zinc-500">(unable to decrypt)</span>}
                </p>
              </div>
            </button>
            <button
              onClick={() => unstar(m.id)}
              disabled={busyId === m.id}
              className="shrink-0 text-amber-400 hover:text-amber-300 transition disabled:opacity-50 px-3 py-2.5"
              title="Unstar"
              aria-label="Unstar message"
            >
              <IconStar size={14} filled />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
